import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { FileCheck2 } from 'lucide-react'
import { listIncidents, verifyIncident } from '../../api/incidents'
import type { Incident } from '../../api/schemas'
import { PageHeader } from '../../components/layout/PageHeader'
import { EmptyState, StatusBadge } from '../../components/common/Status'
import { TimeAgo } from '../../components/common/TimeAgo'
import { IncidentImages } from '../../components/common/JsonImages'
import { Skeleton } from '../../components/ui/skeleton'
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs'
import { Button } from '../../components/ui/button'
import { Textarea } from '../../components/ui/textarea'
import { Label } from '../../components/ui/label'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog'
import { eventTypeLabel } from '../../lib/alert-level'
import { formatCoord } from '../../lib/format'
import { toast } from '../../stores/toast'

const PAGE_SIZE = 24

/** 直报审核：公众上报事件卡流 + 确认/驳回 */
export function IncidentsPage() {
  const [status, setStatus] = useState('pending')
  const query = useQuery({
    queryKey: ['incidents', { status }],
    queryFn: () => listIncidents({ status, page: 1, pageSize: PAGE_SIZE }),
    refetchInterval: 60_000,
    retry: 1,
  })

  const data = query.data
  return (
    <>
      <PageHeader
        breadcrumb="应急处置"
        title="直报审核"
        description="公众通过触/哨端上报的现场事件，审核后计入事件聚合"
      />
      <div className="mb-4">
        <Tabs value={status} onValueChange={setStatus}>
          <TabsList>
            <TabsTrigger value="pending">待审核</TabsTrigger>
            <TabsTrigger value="confirmed">已确认</TabsTrigger>
            <TabsTrigger value="rejected">已驳回</TabsTrigger>
            <TabsTrigger value="all">全部</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {query.isPending ? (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-64 rounded-xl" />
          ))}
        </div>
      ) : query.isError ? (
        <div className="panel-glass rounded-xl">
          <EmptyState title="直报列表加载失败" description={query.error.message} icon={<FileCheck2 size={28} />} />
        </div>
      ) : data && data.items.length > 0 ? (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {data.items.map((inc) => (
            <IncidentCard key={inc.reportId} incident={inc} />
          ))}
        </div>
      ) : (
        <div className="panel-glass rounded-xl">
          <EmptyState
            title="暂无直报"
            description="公众端 POST /api/v1/incidents 上报后在此审核"
            icon={<FileCheck2 size={28} />}
          />
        </div>
      )}
    </>
  )
}

function IncidentCard({ incident }: { incident: Incident }) {
  const qc = useQueryClient()
  const [preview, setPreview] = useState<string | null>(null)
  const [verifyOpen, setVerifyOpen] = useState(false)

  const mutation = useMutation({
    mutationFn: (next: 'confirmed' | 'rejected') => verifyIncident(incident.reportId, next),
    onSuccess: (_, next) => {
      toast.success(next === 'confirmed' ? '已确认直报' : '已驳回直报', incident.reportId)
      setVerifyOpen(false)
      void qc.invalidateQueries({ queryKey: ['incidents'] })
      void qc.invalidateQueries({ queryKey: ['events'] })
    },
    onError: (err: Error) => toast.error('操作失败', err.message),
  })

  return (
    <div className="panel-glass flex flex-col overflow-hidden rounded-xl">
      <IncidentImages raw={incident.photoUrls} className="h-40 rounded-none border-0" onOpen={setPreview} />
      <div className="flex min-h-0 flex-1 flex-col gap-2 p-4">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-foreground">{eventTypeLabel(incident.type)}</span>
          <StatusBadge status={incident.status} />
          <TimeAgo ts={incident.createdAt} className="ml-auto text-[10px] text-muted-foreground" />
        </div>
        <p className="line-clamp-2 min-h-[2rem] text-[11px] leading-4 text-muted-foreground">
          {incident.description || '（无描述）'}
        </p>
        <div className="mt-auto flex items-center justify-between gap-2">
          <span className="font-mono text-[10px] text-muted-foreground/70">
            {incident.deviceId} · {formatCoord(incident.lat, incident.lon)}
          </span>
          {incident.status === 'pending' && (
            <Button size="sm" onClick={() => setVerifyOpen(true)}>
              审核
            </Button>
          )}
        </div>
      </div>

      <Dialog open={Boolean(preview)} onOpenChange={(next) => !next && setPreview(null)}>
        <DialogContent className="max-w-2xl">
          {preview && <img src={preview} alt="现场照片" className="max-h-[70vh] w-full rounded-lg object-contain" />}
        </DialogContent>
      </Dialog>

      <Dialog open={verifyOpen} onOpenChange={setVerifyOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>审核直报 {incident.reportId}</DialogTitle>
            <DialogDescription>{eventTypeLabel(incident.type)} · 确认后计入事件聚合与统计</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="verify-note">备注（可选）</Label>
              <Textarea id="verify-note" placeholder="审核说明…" className="mt-1.5 min-h-[64px]" />
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={mutation.isPending}
                onClick={() => mutation.mutate('rejected')}
              >
                驳回
              </Button>
              <Button
                size="sm"
                disabled={mutation.isPending}
                onClick={() => mutation.mutate('confirmed')}
              >
                确认
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
